import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  RequestWorkflowExternal,
  RequestWorkflowsData,
  WorkflowInstanceKey,
} from '@workflows/types';
import { from, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { WorkflowApiService } from './workflow-api.service';
import { RegistryCounterparty, RequestDTO } from '@core/types';
import { GetEventsOfRequestWorkflowResponse } from '@workflows/types/requestWorkflowEvents/getEventsOfRequestWorkflow';
import {
  WorkflowStorageOwnerService,
  WorkflowStorageRepository,
  workflowInstanceKeyToString,
  type WorkflowStorageOwner,
} from './storage';
import type {
  WorkflowDataLoadResult,
  WorkflowDataLoadState,
  WorkflowFormDraft,
  WorkflowFormDraftReadResult,
} from './workflow-data.types';

/**
 * Сервис для управления данными workflow
 * Отвечает за загрузку, кеширование (с владельцем) и черновики форм
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowDataService {
  private readonly workflowApiService = inject(WorkflowApiService);
  private readonly storage = inject(WorkflowStorageRepository);
  private readonly ownerService = inject(WorkflowStorageOwnerService);

  /**
   * Сигнал с данными workflow для текущей заявки (может обновляться в офлайн режиме, в отличии от entities$ в WorkflowService, который обновляется только при загрузке с API)
   */
  public $requestsWorkflowData = signal<RequestWorkflowsData | null>(null);

  /**
   * Состояние последней загрузки данных регламентов текущей заявки
   */
  public readonly $loadState = signal<WorkflowDataLoadState>('idle');

  /**
   * Observable версия сигнала для использования в reactive streams
   */
  public $requestsWorkflowDataObservable$ = toObservable(
    this.$requestsWorkflowData,
  );

  /**
   * Загрузить данные workflow для заявки.
   * Кэш владельца используется только при недоступности сети (status 0);
   * при 401/403/404 и прочих ответах сервера кэш не показывается.
   */
  loadWorkflowData(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<WorkflowDataLoadResult> {
    return this.workflowApiService.getRequestWorkflowsData(requestNumber).pipe(
      tap(data => {
        void this.saveSnapshot(requestNumber, data);
      }),
      map((data): WorkflowDataLoadResult => ({ state: 'live', data })),
      catchError((error: unknown) => {
        const status = error instanceof HttpErrorResponse ? error.status : null;

        if (status === 0) {
          return from(this.readSnapshot(requestNumber)).pipe(
            map((cachedData): WorkflowDataLoadResult =>
              cachedData
                ? { state: 'stale-offline', data: cachedData }
                : { state: 'unavailable', data: null },
            ),
          );
        }

        console.warn('Failed to fetch workflow data:', error);

        return of<WorkflowDataLoadResult>({
          state: mapHttpStatusToLoadState(status),
          data: null,
        });
      }),
    );
  }

  /**
   * Обновить данные workflow в сигнале и кэше владельца
   */
  updateWorkflowData(
    requestNumber: RequestDTO['requestNumber'],
    data: RequestWorkflowsData,
  ): void {
    this.$requestsWorkflowData.set(data);
    void this.saveSnapshot(requestNumber, data);
  }

  /**
   * Очистить данные workflow в сигнале и кэше для заявки
   */
  clearWorkflowDataForRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): void {
    this.$requestsWorkflowData.set(null);
    void this.clearIndexedDBForRequest(requestNumber);
  }

  /**
   * Очистить кэш данных workflow заявки
   */
  public async clearIndexedDBForRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): Promise<void> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    try {
      await this.storage.delete(owner, 'wf-data', requestNumber);
    } catch (error) {
      console.error('Failed to clear workflow data cache:', error);
    }
  }

  /**
   * Сохранить черновик формы. Ошибка записи пробрасывается — UI должен сообщить,
   * что черновик не сохранён на устройстве.
   */
  public async saveFormDraft(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: string,
    nodeId: number,
    formId: number,
    formValue: Record<string, unknown>,
  ): Promise<void> {
    const owner = this.ownerService.requireOwner();
    const draft: WorkflowFormDraft = {
      formValue,
      savedAt: new Date().toISOString(),
    };

    await this.storage.put(
      owner,
      'wf-draft',
      this.getDraftResourceKey(requestNumber, {
        requestWorkflowId: workflowId,
        nodeId,
        formId,
      }),
      draft,
      {
        requestNumber,
        workflowVersionId: this.getWorkflowVersionId(workflowId),
      },
    );
  }

  /**
   * Прочитать черновик формы. Черновик другой версии регламента не восстанавливается.
   */
  public async getFormDraft(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: string,
    nodeId: number,
    formId: number,
  ): Promise<WorkflowFormDraftReadResult> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return { status: 'none' };
    }

    const resourceKey = this.getDraftResourceKey(requestNumber, {
      requestWorkflowId: workflowId,
      nodeId,
      formId,
    });

    try {
      const stored = await this.storage.get<WorkflowFormDraft>(
        owner,
        'wf-draft',
        resourceKey,
      );

      if (!stored) {
        return { status: 'none' };
      }

      const currentVersionId = this.getWorkflowVersionId(workflowId);

      if (
        stored.workflowVersionId !== undefined &&
        currentVersionId !== undefined &&
        stored.workflowVersionId !== currentVersionId
      ) {
        await this.storage.delete(owner, 'wf-draft', resourceKey);

        return { status: 'discarded' };
      }

      return { status: 'restored', draft: stored.value };
    } catch (error) {
      console.error('Failed to read form draft:', error);

      return { status: 'none' };
    }
  }

  /**
   * Удалить черновик формы (после успешной синхронизации)
   */
  public async removeFormDraft(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: string,
    nodeId: number,
    formId: number,
    explicitOwner?: WorkflowStorageOwner,
  ): Promise<void> {
    const owner = explicitOwner ?? this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    try {
      await this.storage.delete(
        owner,
        'wf-draft',
        this.getDraftResourceKey(requestNumber, {
          requestWorkflowId: workflowId,
          nodeId,
          formId,
        }),
      );
    } catch (error) {
      console.error('Failed to remove form draft:', error);
    }
  }

  public setRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
    mistakes: { mistakeTypeId: number; comment: string }[],
  ) {
    return this.workflowApiService.setRequestMistakes(
      requestNumber,
      requestWorkflowId,
      mistakes,
    );
  }

  public getRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ) {
    return this.workflowApiService.getRequestMistakes(
      requestNumber,
      requestWorkflowId,
    );
  }

  public getMistakesTypes(counterparty: RegistryCounterparty['id']) {
    return this.workflowApiService.getMistakesTypes(counterparty);
  }

  // Получить события регламента заявки
  public getEventsOfRequestWorkflow(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.workflowApiService.getEventsOfRequestWorkflow(
      requestNumber,
      requestWorkflowId,
    );
  }

  // Получить события всех регламентов заявки
  public getEventsOfAllRequestWorkflows(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.workflowApiService.getEventsOfAllRequestWorkflows(
      requestNumber,
    );
  }

  private getDraftResourceKey(
    requestNumber: RequestDTO['requestNumber'],
    instance: WorkflowInstanceKey,
  ): string {
    return workflowInstanceKeyToString(requestNumber, instance);
  }

  /**
   * Версия регламента экземпляра (по requestWorkflowId) из текущих данных
   */
  private getWorkflowVersionId(requestWorkflowId: string): number | undefined {
    return this.$requestsWorkflowData()?.requestsWorkflows.find(
      requestWorkflow => requestWorkflow.id === requestWorkflowId,
    )?.workflowVersionId;
  }

  private async saveSnapshot(
    requestNumber: RequestDTO['requestNumber'],
    data: RequestWorkflowsData,
  ): Promise<void> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return;
    }

    try {
      await this.storage.put(owner, 'wf-data', requestNumber, data, {
        requestNumber,
      });
    } catch (error) {
      // Кэш — не данные пользователя: без снимка просто не будет офлайн-просмотра
      console.error('Failed to save workflow data snapshot:', error);
    }
  }

  private async readSnapshot(
    requestNumber: RequestDTO['requestNumber'],
  ): Promise<RequestWorkflowsData | null> {
    const owner = this.ownerService.getOwner();

    if (!owner) {
      return null;
    }

    try {
      const stored = await this.storage.get<RequestWorkflowsData>(
        owner,
        'wf-data',
        requestNumber,
      );

      return stored?.value ?? null;
    } catch (error) {
      console.error('Failed to read workflow data snapshot:', error);

      return null;
    }
  }
}

function mapHttpStatusToLoadState(
  status: number | null,
): WorkflowDataLoadState {
  switch (status) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    default:
      return 'unavailable';
  }
}
